import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { resetPassword, updatePassword } from '../../lib/auth';
import { supabase } from '../../lib/supabase';

type ForgotPasswordStep = 'email' | 'verification' | 'newPassword' | 'success';

export function ForgotPassword({ onBack }: { onBack: () => void }) {
  const location = useLocation();
  const [step, setStep] = useState<ForgotPasswordStep>('email');
  const [email, setEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Check if we're coming from a password reset link
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const accessToken = urlParams.get('access_token');
    const refreshToken = urlParams.get('refresh_token');
    const type = urlParams.get('type');

    if (type === 'recovery' && accessToken && refreshToken) {
      // We're coming from a password reset link, show the new password step
      setStep('newPassword');
      
      // Set the session with the tokens from the URL
      supabase.auth.setSession({
        access_token: accessToken,
        refresh_token: refreshToken,
      }).catch(err => {
        console.error('Error setting session:', err);
        setError('Invalid or expired reset link. Please request a new one.');
        setStep('email');
      });
    }
  }, [location]);

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      await resetPassword(email);
      setStep('verification');
      setSuccess('Password reset email sent! Check your inbox.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send reset email');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    // Additional password validation
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/;
    if (!passwordRegex.test(newPassword)) {
      setError('Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character');
      return;
    }

    setLoading(true);

    try {
      await updatePassword(newPassword);
      setStep('success');
      setSuccess('Password updated successfully!');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update password');
    } finally {
      setLoading(false);
    }
  };

  const renderEmailStep = () => (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-idecide-dark mb-2">Forgot Password?</h2>
        <p className="text-gray-600">Enter your email address and we'll send you a link to reset your password.</p>
      </div>

      <form onSubmit={handleEmailSubmit} className="space-y-4">
        <div>
          <label htmlFor="resetEmail" className="block text-sm font-medium text-gray-700 mb-1">
            Email Address
          </label>
          <input
            id="resetEmail"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full rounded-md border border-idecide-secondary px-3 py-2 transition-shadow duration-300 focus:border-idecide-primary focus:outline-none focus:ring-2 focus:ring-idecide-primary/50"
            required
            placeholder="Enter your email address"
          />
        </div>

        {error && (
          <div className="text-idecide-error text-sm text-center bg-red-50 p-3 rounded-md">{error}</div>
        )}

        <button
          type="submit"
          disabled={loading}
          className="w-full bg-idecide-primary text-idecide-dark font-semibold rounded-md py-3 transition-colors duration-300 hover:bg-idecide-primary-hover disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? 'Sending...' : 'Send Reset Link'}
        </button>
      </form>

      <div className="text-center">
        <button
          onClick={onBack}
          className="text-idecide-accent hover:text-idecide-accent-hover text-sm font-medium"
        >
          ← Back to Sign In
        </button>
      </div>
    </div>
  );

  const renderVerificationStep = () => (
    <div className="space-y-6">
      <div className="text-center">
        <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
          <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h2 className="text-2xl font-bold text-idecide-dark mb-2">Check Your Email</h2>
        <p className="text-gray-600 mb-4">
          We've sent a password reset link to <strong>{email}</strong>
        </p>
        <p className="text-sm text-gray-500">
          Click the link in your email to continue with password reset.
        </p>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-800">Didn't receive the email?</h3>
            <div className="mt-2 text-sm text-blue-700">
              <p>• Check your spam folder</p>
              <p>• Make sure you entered the correct email address</p>
              <button
                onClick={() => setStep('email')}
                className="text-blue-800 underline hover:text-blue-900"
              >
                Try a different email address
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="text-center">
        <button
          onClick={onBack}
          className="text-idecide-accent hover:text-idecide-accent-hover text-sm font-medium"
        >
          ← Back to Sign In
        </button>
      </div>
    </div>
  );

  const renderNewPasswordStep = () => (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-idecide-dark mb-2">Create New Password</h2>
        <p className="text-gray-600">Enter your new password below.</p>
      </div>

      <form onSubmit={handlePasswordUpdate} className="space-y-4">
        <div>
          <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-1">
            New Password
          </label>
          <input
            id="newPassword"
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            className="w-full rounded-md border border-idecide-secondary px-3 py-2 transition-shadow duration-300 focus:border-idecide-primary focus:outline-none focus:ring-2 focus:ring-idecide-primary/50"
            required
            minLength={8}
            placeholder="Enter your new password"
          />
          <div className="text-xs text-gray-500 mt-1 space-y-1">
            <p>Password must contain:</p>
            <ul className="list-disc list-inside ml-2">
              <li>At least 8 characters</li>
              <li>One uppercase letter</li>
              <li>One lowercase letter</li>
              <li>One number</li>
              <li>One special character (@$!%*?&)</li>
            </ul>
          </div>
        </div>

        <div>
          <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
            Confirm New Password
          </label>
          <input
            id="confirmPassword"
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            className="w-full rounded-md border border-idecide-secondary px-3 py-2 transition-shadow duration-300 focus:border-idecide-primary focus:outline-none focus:ring-2 focus:ring-idecide-primary/50"
            required
            placeholder="Confirm your new password"
          />
        </div>

        {error && (
          <div className="text-idecide-error text-sm text-center bg-red-50 p-3 rounded-md">{error}</div>
        )}

        <button
          type="submit"
          disabled={loading}
          className="w-full bg-idecide-primary text-idecide-dark font-semibold rounded-md py-3 transition-colors duration-300 hover:bg-idecide-primary-hover disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? 'Updating...' : 'Update Password'}
        </button>
      </form>
    </div>
  );

  const renderSuccessStep = () => (
    <div className="space-y-6">
      <div className="text-center">
        <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
          <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h2 className="text-2xl font-bold text-idecide-dark mb-2">Password Updated!</h2>
        <p className="text-gray-600">Your password has been successfully updated.</p>
      </div>

      <button
        onClick={onBack}
        className="w-full bg-idecide-primary text-idecide-dark font-semibold rounded-md py-3 transition-colors duration-300 hover:bg-idecide-primary-hover"
      >
        Continue to Sign In
      </button>
    </div>
  );

  return (
    <div className="min-h-screen bg-idecide-light flex items-center justify-center p-4 animate-fade-in">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-idecide-dark">iDecide</h1>
        </div>
        <div className="bg-white rounded-2xl shadow-xl p-8">
          {step === 'email' && renderEmailStep()}
          {step === 'verification' && renderVerificationStep()}
          {step === 'newPassword' && renderNewPasswordStep()}
          {step === 'success' && renderSuccessStep()}
        </div>
      </div>
    </div>
  );
} 